import { useState } from "react";
import { Link } from "@tanstack/react-router";

const STORAGE_KEY = "cookie-consent-dismissed";

/**
 * Lightweight, dismissible cookie / ad consent banner.
 *
 * - No pre-checked boxes.
 * - Dismissal is stored only after the user clicks the button.
 * - Links to the privacy policy for opt-out details.
 *
 * The banner is server-rendered so it is painted in the first frame instead of
 * popping in after hydration (a late visual change hurts Speed Index). Visitors
 * who already dismissed it never see it: a tiny inline script in the document
 * head sets `data-cc="1"` before paint and CSS hides the banner.
 */
export function CookieConsent() {
  const [visible, setVisible] = useState(true);

  const dismiss = () => {
    setVisible(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
    document.documentElement.setAttribute("data-cc", "1");
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie and advertising consent"
      className="cookie-consent fixed bottom-3 left-3 right-3 z-50 rounded-2xl border border-border bg-background/95 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.12)] backdrop-blur-md sm:bottom-4 sm:left-4 sm:right-4 sm:p-4"
    >
      <div className="mx-auto flex max-w-6xl flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
        <p className="text-xs leading-relaxed text-foreground/90 sm:text-sm">
          <span className="font-semibold text-foreground">We use cookies</span>{" "}
          to serve ads and measure traffic. By using this site, you agree to this. See our{" "}
          <Link to="/privacy" className="underline underline-offset-2 hover:text-primary">
            privacy policy
          </Link>
          .
        </p>
        <div className="flex w-full shrink-0 items-center justify-between gap-2 sm:ml-auto sm:w-auto">
          <a
            href="https://www.google.com/settings/ads"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs underline underline-offset-2 text-foreground/70 hover:text-primary sm:text-sm"
          >
            Ad choices
          </a>
          <button
            type="button"
            onClick={dismiss}
            className="rounded-full bg-primary px-4 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 sm:px-5 sm:py-2 sm:text-sm"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
